import React from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';

interface ConfirmationModalProps {
    open?: boolean;
    title?: string;
    children: React.ReactNode;
    buttonText?: string;
    destructive?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    submitHandler(): Promise<false | any>;
    onModalClose(): void;
}

async function extractErrorMessage(err: unknown): Promise<string> {
    if (err instanceof Response) {
        try {
            const body = await err.json();
            return body?.detail || body?.message || err.statusText;
        } catch {
            return err.statusText || 'Request failed';
        }
    }
    if (err instanceof Error) {
        return err.message;
    }
    return 'An unexpected error occurred';
}

export const ConfirmationModal = ({
    open: initialOpen = false,
    children,
    title,
    buttonText = 'Save',
    destructive,
    onModalClose,
    submitHandler
}: ConfirmationModalProps) => {
    const [open, setOpen] = React.useState(initialOpen);
    const [error, setError] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);

    const handleClose = () => {
        if (submitting) {
            return;
        }
        setOpen(false);
        setError(null);
        onModalClose?.();
    };

    React.useEffect(() => {
        setOpen(initialOpen);
        if (initialOpen) {
            setError(null);
        }
    }, [initialOpen]);

    const handleSave = async () => {
        if (submitting) {
            return;
        }
        setSubmitting(true);
        try {
            setError(null);
            const submit = await submitHandler();

            if (submit != false) {
                onModalClose();
            }
        } catch (err) {
            const message = await extractErrorMessage(err);
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-lg">
                {title && (
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>
                )}
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                <div className="flex flex-col gap-4">{children}</div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={destructive ? 'destructive' : 'default'}
                        onClick={handleSave}
                        loading={submitting}
                    >
                        {buttonText}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
